import { Direction, DIR_DELTA, GridPos } from '../types'
import { gridToWorld } from '../utils/grid'
import { lerp } from '../utils/math'
import type { Maze } from './world/Maze'

/** Discrete grid movement with smooth interpolation between cell centres.
 * Each entity supplies a `chooseNext` callback invoked at every cell centre. */
export class GridMover {
  row: number
  col: number
  dir: Direction = Direction.None
  speed: number
  x = 0
  z = 0
  /** Set true on the frame the mover arrives at a new cell centre. */
  arrived = false

  private fromX = 0
  private fromZ = 0
  private toX = 0
  private toZ = 0
  private progress = 1
  private pending: GridPos

  constructor(
    row: number,
    col: number,
    speed: number,
    private maze: Maze,
    private forEnemy: boolean,
  ) {
    this.row = row
    this.col = col
    this.speed = speed
    this.pending = { row, col }
    this.setCell(row, col)
  }

  setCell(row: number, col: number): void {
    this.row = row
    this.col = col
    const w = gridToWorld(col, row)
    this.x = this.fromX = this.toX = w.x
    this.z = this.fromZ = this.toZ = w.z
    this.progress = 1
    this.pending = { row, col }
    this.arrived = false
  }

  canMove(dir: Direction): boolean {
    const d = DIR_DELTA[dir]
    const nr = this.row + d.dr
    const nc = this.maze.wrapCol(nr, this.col + d.dc)
    return this.maze.isWalkable(nr, nc, this.forEnemy)
  }

  update(dt: number, chooseNext: (cell: GridPos, dir: Direction) => Direction): void {
    this.arrived = false
    let remaining = this.speed * dt
    if (!Number.isFinite(remaining) || remaining < 0) return
    let justWrapped = false
    do {
      if (this.progress >= 1) {
        this.dir = chooseNext({ row: this.row, col: this.col }, this.dir)
        if (this.dir === Direction.None) return
        const d = DIR_DELTA[this.dir]
        const nr = this.row + d.dr
        const nc = this.maze.wrapCol(nr, this.col + d.dc)
        if (!this.maze.isWalkable(nr, nc, this.forEnemy)) {
          this.dir = Direction.None
          return
        }
        if (Math.abs(nc - this.col) > 1) {
          if (justWrapped) return
          this.setCell(nr, nc)
          this.arrived = true
          justWrapped = true
          continue
        }
        this.fromX = this.x
        this.fromZ = this.z
        const w = gridToWorld(nc, nr)
        this.toX = w.x
        this.toZ = w.z
        this.pending = { row: nr, col: nc }
        this.progress = 0
      }

      const step = Math.min(remaining, 1 - this.progress)
      this.progress += step
      remaining -= step
      if (step > 0) justWrapped = false
      if (this.progress >= 1) {
        this.row = this.pending.row
        this.col = this.pending.col
        this.x = this.toX
        this.z = this.toZ
        this.progress = 1
        this.arrived = true
      } else {
        this.x = lerp(this.fromX, this.toX, this.progress)
        this.z = lerp(this.fromZ, this.toZ, this.progress)
      }
    } while (remaining > 0)
  }

  /** Fractional distance to the next cell (0 at centre, →1 approaching next). */
  get cellProgress(): number {
    return this.progress
  }
}
